// Desktop sidebar: the bottom nav's four destinations plus Letters, as a left
// rail, with Feedback and Log out at the foot. Same targets as
// src/components/BottomNavBar.tsx and src/components/LettersQuickAccessCard.tsx,
// same icons, same navy labels. Feedback opens the same modal the phone's
// hamburger menu opens; Log out runs the same steps as the Account screen's
// button (push token, session wipe, routing flags, analytics reset, back to
// Login). Rendered by WebShell only at ≥1024px and only inside the app.
//
// The rail is pinned to the viewport (position: fixed) rather than sticky:
// the frame around the app is only ever one viewport tall (the screens
// overflow it and the document scrolls), and a sticky element cannot leave
// its parent's box, so it scrolled away with the page. A slot of the same
// width stays in flow so the content column keeps its place.

import React, { useCallback, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert } from 'react-native';
import { Image, Pressable, StyleSheet, Text, View } from 'react-native-web';

import { FeedbackModal } from '../../../src/components/FeedbackModal';
import { analytics } from '../../../src/services/analytics';
import { wipeSessionOnLogout } from '../../../src/services/auth/logoutWipe';
import { navigationRef } from '../../../src/services/navigationService';
import { createLogger } from '../../../src/utils/logger';
import type { RootRoute } from './useRootRoute';

import dashboardIcon from '../../../src/assets/icons/dashboard.png';
import tasksIcon from '../../../src/assets/icons/tasks.png';
import docsIcon from '../../../src/assets/icons/docs.png';
import lettersIcon from '../../../src/assets/icons/Email.png';
import profileIcon from '../../../src/assets/icons/profile.png';
import feedbackIcon from '../../../src/assets/icons/communication.png';
import logoutIcon from '../../../src/assets/icons/logout.png';
import logo from '../../../src/assets/logo.png';

const logger = createLogger('Sidebar');

export const SIDEBAR_WIDTH = 240;

interface Item {
  key: 'dashboard' | 'tasks' | 'docs' | 'letters' | 'account';
  label: string;
  icon: string;
  hint: string;
  go: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nav = navigationRef as any;

const ITEMS: Item[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: dashboardIcon,
    hint: 'Go to your dashboard',
    go: () => nav.reset({ index: 0, routes: [{ name: 'Dashboard' }] }),
  },
  {
    key: 'tasks',
    label: 'All Tasks',
    icon: tasksIcon,
    hint: 'View all tasks and checklists',
    go: () => nav.navigate('Dashboard', { screen: 'AllTasks' }),
  },
  {
    key: 'docs',
    label: 'Docs',
    icon: docsIcon,
    hint: 'View and manage your documents',
    go: () => nav.navigate('Documents', { screen: 'DocumentsDashboardScreen' }),
  },
  {
    key: 'letters',
    label: 'Letters',
    icon: lettersIcon,
    hint: 'Your letters to loved ones',
    go: () => nav.navigate('LetterFirst', { screen: 'LettersHome' }),
  },
  {
    key: 'account',
    label: 'Account',
    icon: profileIcon,
    hint: 'View and manage your account settings',
    go: () => nav.navigate('Profile'),
  },
];

export function activeItemFor(route: RootRoute): Item['key'] | null {
  switch (route.root) {
    case 'Dashboard':
      return route.nested === 'AllTasks' ? 'tasks' : 'dashboard';
    case 'Documents':
      return 'docs';
    case 'LetterFirst':
      return 'letters';
    case 'Profile':
      return 'account';
    default:
      return null;
  }
}

async function logOut(): Promise<void> {
  try {
    try {
      const { pushNotificationService } = require('../../../src/services/pushNotificationService');
      await pushNotificationService.removeToken();
    } catch {
      // No push on the web; nothing to remove.
    }
    await wipeSessionOnLogout();
    // The next visit lands on Login, not SignUp (same flags the Account
    // screen sets).
    await AsyncStorage.setItem('@email_verified', 'true');
    await AsyncStorage.setItem('@2fa_setup_complete', 'true');
    analytics.reset();
    nav.reset({
      index: 0,
      routes: [{ name: 'Auth', state: { routes: [{ name: 'Login' }] } }],
    });
  } catch (error) {
    logger.error('Logout failed', error);
    Alert.alert('Error', 'Failed to logout properly');
  }
}

function confirmLogOut(): void {
  Alert.alert('Log out', 'Are you sure you want to log out?', [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Log out', style: 'destructive', onPress: () => void logOut() },
  ]);
}

const footStyle = ({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => [
  styles.item,
  (hovered || pressed) && styles.itemHover,
];

export function Sidebar({ route }: { route: RootRoute }): React.JSX.Element {
  const active = activeItemFor(route);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const onLogOut = useCallback(() => confirmLogOut(), []);
  return (
    <View style={styles.slot}>
    <View style={styles.rail} accessibilityRole="navigation" accessibilityLabel="Main navigation">
      <View style={styles.brand}>
        <Image source={logo} style={styles.logo} accessibilityLabel="FinalKey" />
      </View>
      <View style={styles.items}>
        {ITEMS.map((item) => {
          const isActive = item.key === active;
          return (
            <Pressable
              key={item.key}
              onPress={item.go}
              accessibilityRole="link"
              accessibilityLabel={item.label}
              accessibilityHint={item.hint}
              accessibilityState={{ selected: isActive }}
              style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => [
                styles.item,
                (hovered || pressed) && styles.itemHover,
                isActive && styles.itemActive,
              ]}
            >
              <Image source={item.icon} style={styles.icon} accessibilityElementsHidden />
              <Text style={[styles.label, isActive && styles.labelActive]}>{item.label}</Text>
            </Pressable>
          );
        })}
      </View>
      <View style={styles.foot}>
        <Pressable
          onPress={() => setFeedbackOpen(true)}
          accessibilityRole="button"
          accessibilityLabel="Feedback"
          accessibilityHint="Send feedback to the FinalKey team"
          style={footStyle}
        >
          <Image source={feedbackIcon} style={styles.icon} accessibilityElementsHidden />
          <Text style={styles.label}>Feedback</Text>
        </Pressable>
        <Pressable
          onPress={onLogOut}
          accessibilityRole="button"
          accessibilityLabel="Log out"
          accessibilityHint="Signs you out of FinalKey on this browser"
          style={footStyle}
        >
          <Image source={logoutIcon} style={styles.icon} accessibilityElementsHidden />
          <Text style={styles.label}>Log out</Text>
        </Pressable>
      </View>
      <FeedbackModal visible={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </View>
    </View>
  );
}

const styles = StyleSheet.create({
  // Holds the rail's width in the flex row while the rail itself is fixed.
  slot: {
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    alignSelf: 'stretch',
  },
  rail: {
    width: SIDEBAR_WIDTH,
    // Pinned to the viewport; scrolls its own items if the window is short.
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    overflowY: 'auto',
    zIndex: 10,
    backgroundColor: '#FFFFFF',
    borderRightWidth: 1,
    borderRightColor: '#E5E7EB',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  brand: {
    alignItems: 'center',
    paddingVertical: 12,
    marginBottom: 20,
  },
  logo: {
    width: 120,
    height: 120,
    resizeMode: 'contain',
  },
  items: {
    gap: 4,
  },
  // Pushed to the bottom of the rail, under a hairline, so it never reads
  // as one more destination.
  foot: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 12,
    minHeight: 48,
  },
  itemHover: {
    backgroundColor: '#F3F6F8',
  },
  itemActive: {
    backgroundColor: '#DDF5F5',
  },
  icon: {
    width: 26,
    height: 26,
    resizeMode: 'contain',
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C2C4F',
  },
  labelActive: {
    color: '#1C2C4F',
  },
});
